import { apiService } from './api';
import { AuthResponse, LoginCredentials, RegisterData, User } from '../types';
import { isMockMode, mockDelay, mockLog } from '../utils/mockHelper';
import { getMockUserByEmail, mockUser } from '../data/mockData';

export class AuthService {
  private async retryLogin(credentials: LoginCredentials, attempt = 1): Promise<AuthResponse> {
    const maxAttempts = 2;
    
    // Check for mock mode first
    if (isMockMode()) {
      return this.mockLogin(credentials);
    }
    
    try {
      console.log(`🔄 AuthService: Attempting login (attempt ${attempt}/${maxAttempts})...`, credentials.email);
      const response = await apiService.post<any>('/auth/login', credentials);
      console.log('✅ AuthService: Login response:', response.data);
      
      // Handle the actual API response format: {message, user, token}
      const apiData = response.data;
      const authData: AuthResponse = {
        access_token: apiData.token,
        token_type: 'Bearer',
        expires_in: 3600, // Default 1 hour
        user: apiData.user
      };
      
      // Store token and user data
      apiService.setAuthToken(authData.access_token);
      localStorage.setItem('user', JSON.stringify(authData.user));
      
      console.log('✅ AuthService: Login successful, token stored');
      return authData;
    } catch (error: any) {
      console.error(`❌ AuthService: Login attempt ${attempt} failed:`, error);
      
      // Check if it's a timeout error and we have retries left
      if (attempt < maxAttempts && (error.code === 'ECONNABORTED' || error.message?.includes('timeout'))) {
        console.log(`⏳ AuthService: Timeout detected, retrying (${attempt + 1}/${maxAttempts})...`);
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
        return this.retryLogin(credentials, attempt + 1);
      }
      
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  }

  private async mockLogin(credentials: LoginCredentials): Promise<AuthResponse> {
    mockLog('login attempt', credentials.email);
    
    // Simulate network delay
    await mockDelay();
    
    // Check demo credentials
    if (credentials.email === 'tourist@jeridigital.com' && credentials.password === 'tourist123') {
      const mockToken = 'mock_token_' + Date.now();
      const authData: AuthResponse = {
        access_token: mockToken,
        token_type: 'Bearer',
        expires_in: 3600,
        user: mockUser
      };
      
      // Store mock auth data
      localStorage.setItem('auth_token', mockToken);
      localStorage.setItem('user', JSON.stringify(mockUser));
      apiService.setAuthToken(mockToken);
      
      mockLog('login successful', mockUser);
      return authData;
    }
    
    // Invalid credentials
    throw new Error('Credenciais inválidas');
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    return this.retryLogin(credentials);
  }

  async register(userData: RegisterData): Promise<AuthResponse> {
    if (isMockMode()) {
      return this.mockRegister(userData);
    }
    
    try {
      console.log('🔄 AuthService: Attempting registration...', userData.email);
      const response = await apiService.post<any>('/auth/register', userData);
      console.log('✅ AuthService: Registration response:', response.data);
      
      // Handle the actual API response format: {message, user, token}
      const apiData = response.data;
      const authData: AuthResponse = {
        access_token: apiData.token,
        token_type: 'Bearer',
        expires_in: 3600, // Default 1 hour
        user: apiData.user
      };
      
      // Store token and user data
      apiService.setAuthToken(authData.access_token);
      localStorage.setItem('user', JSON.stringify(authData.user));
      
      console.log('✅ AuthService: Registration successful, token stored');
      return authData;
    } catch (error: any) {
      console.error('❌ AuthService: Registration failed:', error);
      
      // Better error handling for validation errors
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat().join(', ');
        throw new Error(errorMessages);
      }
      
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  }

  private async mockRegister(userData: RegisterData): Promise<AuthResponse> {
    mockLog('register attempt', userData.email);
    
    // Simulate network delay
    await mockDelay();
    
    // In mock mode, just return a success but don't auto-login
    throw new Error('Registro realizado com sucesso! Faça login para continuar.');
  }

  async logout(): Promise<void> {
    if (isMockMode()) {
      mockLog('logout');
      this.clearAuth();
      return;
    }
    
    try {
      await apiService.post('/auth/logout');
    } catch (error) {
      // Even if the API call fails, we should clear local storage
      console.warn('Logout API call failed, clearing local storage anyway');
    } finally {
      apiService.removeAuthToken();
    }
  }

  async getProfile(): Promise<User> {
    if (isMockMode()) {
      mockLog('get profile');
      await mockDelay(200, 500);
      return mockUser;
    }
    
    try {
      console.log('🔄 AuthService: Fetching user profile...');
      // Use the correct endpoint from the API routes
      const response = await apiService.get<User>('/auth/me');
      console.log('✅ AuthService: Profile fetched successfully:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ AuthService: Failed to get user profile:', error);
      throw new Error(error.response?.data?.message || 'Failed to get user profile');
    }
  }

  async loginWithGoogle(accessToken: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      console.log('🔄 AuthService: Attempting Google login...');
      
      const response = await apiService.post<any>('/auth/google', {
        access_token: accessToken
      });
      
      console.log('✅ AuthService: Google login response:', response.data);
      
      // Handle the actual API response format: {message, user, token}
      const apiData = response.data;
      const authData: AuthResponse = {
        access_token: apiData.token,
        token_type: 'Bearer',
        expires_in: 3600, // Default 1 hour
        user: apiData.user
      };
      
      // Store token and user data
      localStorage.setItem('auth_token', authData.access_token);
      localStorage.setItem('user', JSON.stringify(authData.user));
      
      console.log('✅ AuthService: Google login successful, user:', authData.user);
      return {
        success: true,
        data: authData
      };
    } catch (error: any) {
      console.error('❌ AuthService: Google login failed:', error);
      
      const errorMessage = error.response?.data?.message || 'Erro ao fazer login com Google';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  async forgotPassword(email: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      console.log('🔄 AuthService: Requesting password reset for:', email);
      
      const response = await apiService.post<{ message: string }>('/auth/forgot-password', {
        email
      });

      console.log('✅ AuthService: Password reset request successful');
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('❌ AuthService: Password reset request failed:', error);
      
      const errorMessage = error.response?.data?.message || 'Erro ao solicitar recuperação de senha';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  clearAuth(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    apiService.removeAuthToken();
  }

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        console.error('Error parsing user data from localStorage');
        return null;
      }
    }
    return null;
  }

  getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }
}

export const authService = new AuthService();
